// What the eyes are looking at, as a request the eye controller resolves per eye.

import { vector3 } from '../../math/index.ts'
import type { Vector3 } from '../../math/index.ts'
import type { EyeSide } from '../eyeSide.ts'
import { DEFAULT_GAZE_LIMITS, clampYawPitch, directionFromYawPitch, limitToCone } from './gazeLimits.ts'
import type { GazeLimits, YawPitch } from './gazeLimits.ts'

export type GazeRequest =
  | { kind: 'rest' }
  | { kind: 'point', point: Vector3 }
  | { kind: 'direction', direction: Record<EyeSide, Vector3> }
  | { kind: 'yawPitch', angles: Record<EyeSide, YawPitch> }

export type GazeControllerOptions = {
  limits?: GazeLimits
  request?: GazeRequest
}

const NO_ANGLES: Record<EyeSide, YawPitch> = {
  left: { yawDegrees: 0, pitchDegrees: 0 },
  right: { yawDegrees: 0, pitchDegrees: 0 },
}

export type GazeController = ReturnType<typeof createGazeController>

export function createGazeController(options: GazeControllerOptions = {}) {
  const limits = options.limits ?? DEFAULT_GAZE_LIMITS
  let request: GazeRequest = options.request ?? { kind: 'rest' }

  const bothSides = <T>(value: T | Record<EyeSide, T>): Record<EyeSide, T> => (
    value && typeof value === 'object' && 'left' in (value as object)
      ? value as Record<EyeSide, T>
      : { left: value as T, right: value as T }
  )

  return {
    limits,

    get request(): GazeRequest { return request },

    /** Look at a world point — the camera, a prop, a conversation target. */
    lookAtPoint(point: Vector3): void {
      request = { kind: 'point', point }
    },

    /** Look along an explicit world direction, shared or per eye. */
    lookAlong(direction: Vector3 | Record<EyeSide, Vector3>): void {
      const perSide = bothSides(direction)
      request = {
        kind: 'direction',
        direction: { left: vector3.normalize(perSide.left), right: vector3.normalize(perSide.right) },
      }
    },

    /** Authoring and animation path: angles off each eye's own rest axis. */
    setYawPitch(angles: YawPitch | Record<EyeSide, YawPitch>): void {
      const perSide = bothSides(angles)
      request = {
        kind: 'yawPitch',
        angles: {
          left: clampYawPitch(perSide.left, limits),
          right: clampYawPitch(perSide.right, limits),
        },
      }
    },

    /** Back to each eye's own optical axis. */
    rest(): void {
      request = { kind: 'rest' }
    },

    /** The yaw/pitch currently requested, for a panel to display. Zero in every other mode. */
    angles(): Record<EyeSide, YawPitch> {
      return request.kind === 'yawPitch' ? request.angles : NO_ANGLES
    },

    /** The limited world direction this eye should look along. */
    resolve(side: EyeSide, restAxis: Vector3, pivot: Vector3): Vector3 {
      const target = resolveUnlimited(request, side, restAxis, pivot)
      return limitToCone(restAxis, target, limits.coneDegrees)
    },
  }
}

function resolveUnlimited(request: GazeRequest, side: EyeSide, restAxis: Vector3,
                          pivot: Vector3): Vector3 {
  if (request.kind === 'point') return vector3.direction(pivot, request.point)
  if (request.kind === 'direction') return request.direction[side]
  if (request.kind === 'yawPitch') return directionFromYawPitch(restAxis, request.angles[side])
  return vector3.normalize(restAxis)
}
